import React, { useState, useEffect } from 'react';
import { BarChart2, Eye, ArrowRight, Stethoscope } from 'lucide-react';
import content from '../../translations/content.json';
import vocabVisionImage from '../../assets/images/vocab-vision.png';
import vocabCareImage from '../../assets/images/vocab-care.png';
import './style.css';

const SoundwaveEqualizerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="18" x2="4" y2="6" />
    <line x1="8" y1="22" x2="8" y2="2" />
    <line x1="12" y1="18" x2="12" y2="6" />
    <line x1="16" y1="22" x2="16" y2="2" />
    <line x1="20" y1="18" x2="20" y2="6" />
  </svg>
);

const LargePurpleSoundwave = () => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
    <path d="M0 100 V 50 L 5 55 L 10 45 L 15 60 L 20 40 L 25 65 L 30 35 L 35 70 L 40 30 L 45 75 L 50 25 L 55 80 L 60 20 L 65 85 L 70 15 L 75 90 L 80 10 L 85 95 L 90 5 L 95 100 L 100 0" fill="none" stroke="#7C3AED" strokeWidth="2" strokeOpacity="0.2" />
    <path d="M0 100 V 60 L 5 65 L 10 55 L 15 70 L 20 50 L 25 75 L 30 45 L 35 80 L 40 40 L 45 85 L 50 35 L 55 90 L 60 30 L 65 95 L 70 25 L 75 100 L 80 20 L 85 100 L 90 15 L 95 100 L 100 10" fill="none" stroke="#7C3AED" strokeWidth="2" strokeOpacity="0.2" />
  </svg>
);

const InsightCardChartLine = () => (
  <svg viewBox="0 0 100 40" className="w-full h-full">
    <defs>
      <linearGradient id="insightCardGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EC4899" stopOpacity="0.4"/>
        <stop offset="100%" stopColor="#EC4899" stopOpacity="0"/>
      </linearGradient>
    </defs>
    <path d="M0 30 C 20 10, 40 35, 60 20, 80 5, 100 25" stroke="#EC4899" fill="url(#insightCardGradient)" strokeWidth="2.5"/>
  </svg>
);

const TypingText = ({ text, startTyping }) => {
  const [displayedText, setDisplayedText] = useState(text);

  useEffect(() => {
    if (startTyping) {
      let i = 0;
      const typingInterval = setInterval(() => {
        setDisplayedText(text.substring(0, i + 1));
        i++;
        if (i > text.length) {
          clearInterval(typingInterval);
        }
      }, 20);

      return () => clearInterval(typingInterval);
    } else {
      setDisplayedText(text);
    }
  }, [startTyping, text]);

  return <p className="platform-card-description">{displayedText}</p>;
};

const PlatformOverview = () => {
  const { platform } = content;
  const [hoveredCardId, setHoveredCardId] = useState(null);

  const cardData = [
    {
      id: 'voice',
      icon: SoundwaveEqualizerIcon,
      badgeClass: "bg-gradient-to-br from-[#5E17EB] to-[#7C3AED] shadow-purple-500/20",
      titleClass: "text-[#5E17EB]",
      subtitleClass: "text-slate-500",
      graphic: <LargePurpleSoundwave />,
      graphicClass: "absolute bottom-4 right-4 w-32 h-20 pointer-events-none z-0 opacity-80",
    },
    {
      id: 'insight',
      icon: BarChart2,
      badgeClass: "bg-gradient-to-br from-[#E11D48] to-[#EC4899] shadow-pink-500/20",
      titleClass: "text-[#E11D48]",
      subtitleClass: "text-slate-500",
      graphic: (
        <div className="absolute bottom-4 right-4 bg-white border border-slate-100 rounded-2xl p-1 shadow-lg shadow-slate-200/60 w-32 h-20 z-0 pointer-events-none overflow-hidden">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Quality Score</p>
          <p className="text-base font-black text-slate-900 my-0">{platform.cards[1].stat.value}</p>
          <div className="relative h-8 w-full">
            <InsightCardChartLine />
          </div>
        </div>
      ),
      graphicClass: "",
    },
    {
      id: 'vision',
      icon: Eye,
      badgeClass: "bg-gradient-to-br from-[#FF6B00] to-[#FF3D00] shadow-orange-500/20",
      titleClass: "text-[#FF6B00]",
      subtitleClass: "text-slate-500",
      graphic: (
        <div className="absolute bottom-4 right-4 w-32 h-20 rounded-xl overflow-hidden border border-slate-200 shadow-md z-0 pointer-events-none">
          <img src={vocabVisionImage} alt="Technician" className="w-full h-full object-cover" />
          <div className="absolute top-2 left-2 w-5 h-5 border-t-2 border-l-2 border-emerald-400/80 rounded-tl-md"></div>
          <div className="absolute bottom-2 right-2 w-5 h-5 border-b-2 border-r-2 border-emerald-400/80 rounded-br-md"></div>
        </div>
      ),
      graphicClass: "",
    },
    {
      id: 'care',
      icon: Stethoscope,
      badgeClass: "bg-gradient-to-br from-[#0EA5E9] to-[#2563EB] shadow-sky-500/20",
      titleClass: "text-[#0EA5E9]",
      subtitleClass: "text-slate-500",
      graphic: (
        <div className="absolute bottom-4 right-4 w-32 h-20 rounded-xl overflow-hidden border border-slate-200 shadow-md z-0 pointer-events-none">
          <img src={vocabCareImage} alt="Medical UI" className="w-full h-full object-cover" />
        </div>
      ),
      graphicClass: "",
    },
  ];

  return (
    <section className="platform-overview-section">
      <div className="platform-overview-container">
        <div className="platform-overview-header">
          <div className="platform-overview-header-text">
            <div className="platform-overview-subtitle-container">
              <span>::</span>
              <span>{platform.subtitle}</span>
            </div>
            <h2 className="platform-overview-heading">{platform.heading}</h2>
            <p className="platform-overview-description">{platform.description}</p>
          </div>
          <a href="#" className="platform-overview-explore-link">
            {platform.exploreLink}
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {platform.cards.map((card) => {
            const cardInfo = cardData.find(c => c.id === card.id);
            if (!cardInfo) return null;
            const CurrentIcon = cardInfo.icon;

            const isHovered = hoveredCardId === card.id;
            const dimClass = hoveredCardId !== null && !isHovered ? 'opacity-50 grayscale' : '';

            return (
              <div
                key={card.id}
                className={`platform-card flex flex-col transition-all duration-300 ease-in-out
                  ${isHovered ? 'shadow-xl scale-[1.02]' : ''}
                  ${dimClass}
                `}
                onMouseEnter={() => setHoveredCardId(card.id)}
                onMouseLeave={() => setHoveredCardId(null)}
              >
                <div className="flex-grow pb-20">
                  <div className="platform-card-header">
                    <div className={`platform-card-icon-container ${cardInfo.badgeClass}`}>
                      <CurrentIcon className="platform-card-icon" />
                    </div>
                    <div>
                      <h3 className={`platform-card-title ${cardInfo.titleClass}`}>{card.title}</h3>
                      <p className={`platform-card-tagline ${cardInfo.subtitleClass}`}>{card.tagline}</p>
                    </div>
                  </div>
                  <TypingText text={card.desc} startTyping={isHovered} />
                </div>

                <div className={cardInfo.graphicClass}>
                  {cardInfo.graphic}
                </div>

                <div className="mt-auto z-10 relative">
                    <a href="#" className={`platform-card-link ${cardInfo.titleClass}`}>
                        {card.link} <ArrowRight className="w-3 h-3" />
                    </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PlatformOverview;
